import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTriggerFormInit,
  useNodeDropdownLabels,
  buildShipmentFilterUpdate,
} from '../shared';
import { Form, Flex } from 'antd';
import useToken from '@/common/helper/useToken';
import { usePaginatedFilters } from '@/common/helper/usePaginatedFilters';
import { ALERT_TRIGGER_FILTER_CONFIGS } from '@/common/helper/filterConfigs';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { useFetchCompanyCouriers } from '@/hooks';
import { ETAChangedByList } from '@/common/static_data';
import { CustomSelect, CustomShipmentText } from '@/components';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { type DropdownLabelItem } from '@/apis/logiblocksLabelsAPI';
import { ShipmentFilterApply } from '../alertTrigger/components';
import { DownOutlined } from '@ant-design/icons';

import { ShipmentFilterFormData } from '../nodeFormTypes';

interface ETAChangeTriggerFormData extends ShipmentFilterFormData {
  change_by?: string;
  direction?: string;
}

const ETA_DIRECTION_OPTIONS = [
  { value: '', label: 'Either earlier or later' },
  { value: 'later', label: 'Later only' },
  { value: 'earlier', label: 'Earlier only' },
];

/**
 * Customization UI for ETA_CHANGE_TRIGGER nodes.
 */
export const ETAChangeTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  nodeId,
  workflowId,
  isReadOnly = false,
}) => {
  const [form] = Form.useForm();
  const { getToken } = useToken();
  const { couriers } = useFetchCompanyCouriers();
  const [teamsList, setTeamsList] = useState([]);
  const [applyType, setApplyType] = useState([]);

  // Pre-loaded labels for paginated dropdowns (fetched once when panel opens).
  const preloadedLabels = useNodeDropdownLabels({
    isSidePannelOpen,
    workflowId,
    nodeId,
    getToken,
  });

  // --- Teams -------------------------------------------------------------

  useEffect(() => {
    getTeamsDDLOFFeature('', 'Shipment').then((val) => {
      if (val && val.length > 0) setTeamsList(val);
    });
  }, []);

  // --- Paginated filters -------------------------------------------------

  const {
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  } = usePaginatedFilters(ALERT_TRIGGER_FILTER_CONFIGS, getToken);

  // --- Merge helper ------------------------------------------------------

  /**
   * Merge helper: prepend pre-loaded label items that are not already present
   * in the paginated page-1 data. Page data takes precedence (comes first so
   * the selector deduplication logic keeps the richer version).
   */
  const mergeWithPreloaded = useCallback(
    (pageData: unknown[], preloaded: DropdownLabelItem[]): unknown[] => {
      if (preloaded.length === 0) return pageData;
      const pageIds = new Set(
        (pageData as Array<{ id?: string | number }>).map((item) =>
          String(item.id ?? '')
        )
      );
      const extra = preloaded.filter((item) => !pageIds.has(String(item.id)));
      return [...pageData, ...extra];
    },
    []
  );

  // Memoized paginated data enriched with pre-loaded labels for off-page values.
  const memoizedOriginsData = useMemo(
    () => mergeWithPreloaded(getFilterData(3), preloadedLabels.origins),
    [getFilterData, mergeWithPreloaded, preloadedLabels.origins]
  );
  const memoizedProductsData = useMemo(
    () => mergeWithPreloaded(getFilterData(1), preloadedLabels.products),
    [getFilterData, mergeWithPreloaded, preloadedLabels.products]
  );
  const memoizedDestinationsData = useMemo(
    () => mergeWithPreloaded(getFilterData(4), preloadedLabels.destinations),
    [getFilterData, mergeWithPreloaded, preloadedLabels.destinations]
  );

  const applyConfig = useMemo(
    () =>
      getApplyConfig({
        teamsList,
        origins: memoizedOriginsData,
        products: memoizedProductsData,
        couriers,
        destinations: memoizedDestinationsData,
      }),
    [
      teamsList,
      memoizedOriginsData,
      memoizedProductsData,
      couriers,
      memoizedDestinationsData,
    ]
  );

  // --- Node update handlers ----------------------------------------------

  const handleNodeData = (
    data: ETAChangeTriggerFormData,
    isSelectAll?: boolean
  ) => {
    if (!data || Object.keys(data).length === 0) return;
    onChange({
      eta_change_trigger: buildShipmentFilterUpdate(
        data,
        nodeData.eta_change_trigger,
        applyType,
        {
          change_by:
            data.change_by ?? nodeData.eta_change_trigger?.change_by ?? '',
          direction:
            data.direction ?? nodeData.eta_change_trigger?.direction ?? '',
        },
        isSelectAll
      ),
    });
  };

  const handleChangeByChange = useCallback(
    (value: string) => {
      onChange({
        eta_change_trigger: {
          ...nodeData.eta_change_trigger,
          change_by: value,
        },
      });
    },
    [nodeData.eta_change_trigger, onChange]
  );

  const handleDirectionChange = useCallback(
    (value: string) => {
      onChange({
        eta_change_trigger: {
          ...nodeData.eta_change_trigger,
          direction: value,
        },
      });
    },
    [nodeData.eta_change_trigger, onChange]
  );

  // --- Form initialization -----------------------------------------------

  const hasPatchedRef = useTriggerFormInit({
    isSidePannelOpen,
    triggerData: nodeData?.eta_change_trigger,
    form,
    setApplyType,
    extraFormValues: {
      change_by: nodeData?.eta_change_trigger?.change_by ?? undefined,
      direction: nodeData?.eta_change_trigger?.direction ?? '',
    },
  });

  // Reset the form-init patch flag on panel close so re-opening re-initializes.
  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen, hasPatchedRef]);

  return (
    <Form form={form} layout='vertical'>
      <Flex vertical gap={16}>
        <Flex vertical>
          <CustomShipmentText
            text={'ETA changes by more than'}
            fontSize='14px'
            fontWeight={600}
            marginBottom={'4px'}
          />
          <Form.Item
            name='change_by'
            rules={[{ required: true, message: 'Please select an ETA change' }]}
            style={{ marginBottom: '10px' }}
          >
            <CustomSelect
              width='100%'
              suffixIcon={
                <DownOutlined style={{ color: 'var(--text-secondary)' }} />
              }
              placeholder='Select...'
              options={ETAChangedByList}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='36px'
              border='none'
              placeholderColor='var(--text-secondary)'
              onChange={handleChangeByChange}
              disabled={isReadOnly}
            />
          </Form.Item>
        </Flex>
        <Flex vertical>
          <CustomShipmentText
            text={'ETA moves'}
            fontSize='14px'
            fontWeight={600}
            marginBottom={'4px'}
          />
          <Form.Item name='direction' style={{ marginBottom: '10px' }}>
            <CustomSelect
              width='100%'
              suffixIcon={
                <DownOutlined style={{ color: 'var(--text-secondary)' }} />
              }
              options={ETA_DIRECTION_OPTIONS}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='36px'
              border='none'
              onChange={handleDirectionChange}
              disabled={isReadOnly}
            />
          </Form.Item>
        </Flex>
        <ShipmentFilterApply
          applyType={applyType}
          setApplyType={setApplyType}
          form={form}
          applyConfig={applyConfig}
          handleNodeUpdate={handleNodeData}
          isReadOnly={isReadOnly}
          origins={memoizedOriginsData}
          originsLoading={getFilterLoading(3)}
          originsHasMore={getFilterHasMore(3)}
          onLoadMoreOrigins={getFilterLoadMore(3)}
          products={memoizedProductsData}
          productsLoading={getFilterLoading(1)}
          productsHasMore={getFilterHasMore(1)}
          onLoadMoreProducts={getFilterLoadMore(1)}
          destinations={memoizedDestinationsData}
          destinationsLoading={getFilterLoading(4)}
          destinationsHasMore={getFilterHasMore(4)}
          onLoadMoreDestinations={getFilterLoadMore(4)}
        />
      </Flex>
    </Form>
  );
};
